import React, { createContext, useState, useEffect, ReactNode } from "react";
import { User } from "./types";
import {
  authenticateUser,
  readUser,
  logoutUser,
  saveUser,
  fetchAdvisorTPV,
} from "./AuthService";
import usePermissions from "./usePermissions";
interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (credentials: { user: string; password: string }) => Promise<void>;
  logout: (onLogout?: () => void) => Promise<void>
  validLoggin: () => boolean;
  updateuserTerminal: (terminal: string) => Promise<void>;
  validPermissionsbyroute: (route: string) => boolean
  getRoutePermissions: (route: string) => any;
  updateuserTPV: (tpv: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Provides authentication state and helpers for descendant components.
 *
 * @param children - React elements to be wrapped by the provider.
 * @returns The provider element that supplies the auth context.
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const { validPermissionsbyroute, getRoutePermissions } = usePermissions({ user });
  useEffect(() => {
    const fetchUser = async () => {
      const userDoc = await readUser();
      if (userDoc) {
        setUser(userDoc.user);
        setToken(userDoc.user.token);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const ensureAdvisorTPV = async () => {
      if (!user || !token || user.tpv) return;
      const assignedTpv = await fetchAdvisorTPV(user.id, token);
      if (!assignedTpv) return;
      const updatedUser = { ...user, tpv: assignedTpv };
      await saveUser(updatedUser);
      setUser(updatedUser);
    };

    ensureAdvisorTPV();
  }, [user, token]);

  const login = async (credentials: { user: string; password: string }) => {
    await authenticateUser(credentials);
    const userDoc = await readUser();
    if (userDoc) {
      setUser(userDoc.user);
      setToken(userDoc.user.token);
    }
  };

  const logout = async (onLogout = () => { }) => {
    await logoutUser();
    setUser(null);
    setToken(null);
    onLogout();
  };

  const validLoggin = () => {
    if (user) {
      const lifeToken = user.lifeToken;
      const lifeTokenDate = new Date(lifeToken);
      const currentDate = new Date();
      if (!(currentDate < lifeTokenDate)) {
        logout();
        return false;
      }
      return true;
    }
    return false;
  };
  const updateuserTerminal = async (terminal: string) => {
    if (user) {
      const copyuser = { ...user, terminal: terminal };
      try {
        await saveUser(copyuser);
        setUser(copyuser);
      } catch (error) {
        console.log(error);
      }

    }
  }

  const updateuserTPV = async (tpv: string) => {
    if (user) {
      const copyuser = { ...user, tpv: tpv };
      try {
        await saveUser(copyuser);
        setUser(copyuser);
      } catch (error) {
        console.log(error);
      }

    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        validLoggin,
        updateuserTerminal,
        validPermissionsbyroute,
        getRoutePermissions,
        updateuserTPV
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Access the authentication context.
 *
 * @throws Error if the hook is used outside of `AuthProvider`.
 * @returns The current authentication context value.
 */
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
